import { useEffect, useMemo, useRef, useState, type Key } from "react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CROSS_ICON, SEARCH_ICON } from "src/assets/svgs";
import {
  CustomButton,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import { useQueryGetVehicleList } from "src/queries/vehicles.queries";
import type { VehicleListResponse } from "src/services/vehicleService";
import type { Customer } from "src/services/customerService";
import DevicesTable from "./DevicesTable";
import MoveVehiclesModal from "./MoveVehiclesModal";
import { canMoveCustomerVehicles } from "./vehicleMove.helpers";

const SearchIcon = SEARCH_ICON as unknown as ComponentType<{
  className?: string;
}>;

const CrossIcon = CROSS_ICON as unknown as ComponentType<{
  className?: string;
}>;

interface DevicesTabProps {
  clientId?: string;
  currentCustomer?: Customer | null;
}

interface DevicesSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

export interface CustomerDevice {
  unitId: string;
  vehicleId: string;
  id: string;
  vehicle: string;
  vin: string;
  licensePlate: string;
  vehicleType: string;
  lastDataUpdate: number | string;
  fuelSensors: number | string;
  ignition: number | string;
  installedOn: string;
  firmware: string;
}

type DeviceSource = Record<string, unknown>;
type DeviceColumnFilters = Record<string, string>;

const readNestedValue = (value: unknown, key: string) => {
  if (value && typeof value === "object" && key in value) {
    return (value as Record<string, unknown>)[key];
  }

  return undefined;
};

const asDeviceSource = (value: unknown): DeviceSource =>
  value && typeof value === "object" ? (value as DeviceSource) : {};

const readNestedName = (value: unknown) => {
  if (value && typeof value === "object" && "name" in value) {
    return String((value as { name?: unknown }).name ?? "");
  }

  return String(value ?? "");
};

const readFirstValue = (
  record: DeviceSource,
  keys: string[],
): string | number | undefined => {
  for (const key of keys) {
    const value = record[key];

    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      (typeof value === "string" || typeof value === "number")
    ) {
      return value;
    }
  }

  return undefined;
};

const pickArray = (value: unknown): DeviceSource[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (item): item is DeviceSource => !!item && typeof item === "object",
  );
};

const getVehicleListItems = (
  response?: VehicleListResponse | unknown[],
): DeviceSource[] => {
  if (Array.isArray(response)) return pickArray(response);

  if (pickArray(response?.vehicleInfos).length > 0) {
    return pickArray(response?.vehicleInfos);
  }

  if (pickArray(response?.vehicles).length > 0) {
    return pickArray(response?.vehicles);
  }

  if (pickArray(response?.items).length > 0) {
    return pickArray(response?.items);
  }

  if (pickArray(response?.data).length > 0) {
    return pickArray(response?.data);
  }

  return pickArray(response?.content);
};

const getVehicleTotalCount = (
  response: VehicleListResponse | unknown[] | undefined,
  fallback: number,
) => {
  if (!response || Array.isArray(response)) return fallback;

  return Number(
    response.totalItems ?? response.totalElements ?? response.total ?? fallback,
  );
};

const mapDeviceSourceToCustomerDevice = (
  item: DeviceSource,
  index: number,
): CustomerDevice => {
  const vehicle = readFirstValue(item, [
    "vehicle",
    "vehicleName",
    "vehicleModel",
    "model",
    "name",
    "label",
  ]);

  const vehicleType = readFirstValue(item, [
    "vehicleType",
    "type",
    "unitType",
  ]);

  const installedOn = readFirstValue(item, [
    "installedOn",
    "installedAt",
    "installationDate",
    "installDate",
    "createDate",
  ]);
  const vehicleDetails = item.vehicleDetails;
  const vehicleRecord = item.vehicle;
  const deviceRecord = asDeviceSource(item.device);
  const unitRecord = asDeviceSource(deviceRecord.unit ?? item.unit);
  const vehicleId =
    readFirstValue(item, ["vehicleId", "id"]) ??
    readNestedValue(vehicleDetails, "id") ??
    readNestedValue(vehicleRecord, "id");
  const unitId =
    readFirstValue(item, ["unitId"]) ??
    readFirstValue(unitRecord, ["id"]) ??
    readFirstValue(deviceRecord, ["unitId"]) ??
    readFirstValue(item, ["deviceId"]) ??
    readFirstValue(deviceRecord, ["id"]);

  return {
    unitId: String(unitId ?? ""),
    vehicleId: String(vehicleId ?? ""),
    id: String(
      readFirstValue(item, ["imei", "id", "unitId", "deviceId"]) || index,
    ),
    vehicle: readNestedName(vehicle) || "--",
    vin: String(readFirstValue(item, ["vin", "VIN"]) || "--"),
    licensePlate: String(
      readFirstValue(item, [
        "licensePlate",
        "licencePlate",
        "plate",
        "registrationNumber",
      ]) || "--",
    ),
    vehicleType: readNestedName(vehicleType) || "--",
    lastDataUpdate: readFirstValue(item, ["lastDataUpdate", "lastDate"]) ?? "--",
    fuelSensors: readFirstValue(item, ["fuelSensors", "fuelSensor"]) ?? "--",
    ignition: readFirstValue(item, ["ignition"]) ?? "--",
    installedOn: String(installedOn || "--"),
    firmware: String(readFirstValue(item, ["firmware", "version"]) || "--"),
  };
};

const getCustomerDevices = (
  response?: VehicleListResponse | unknown[],
): CustomerDevice[] =>
  getVehicleListItems(response).map((item, index) =>
    mapDeviceSourceToCustomerDevice(item, index),
  );

const DevicesTab = ({ clientId, currentCustomer }: DevicesTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [deviceSearch, setDeviceSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deviceColumnFilters, setDeviceColumnFilters] =
    useState<DeviceColumnFilters>({});
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Key[]>([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const moveAuthorized = canMoveCustomerVehicles();

  const isDeviceSearchTypingRef = useRef(false);

  const [devicesPagination, setDevicesPagination] = useState({
    page: 0,
    size: 10,
  });

  const [devicesSorting, setDevicesSorting] = useState<DevicesSorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });

  useEffect(() => {
    setSelectedVehicleIds([]);
    setMoveModalOpen(false);
  }, [clientId]);

  useEffect(() => {
    if (!isDeviceSearchTypingRef.current) return;

    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(deviceSearch.trim());
      setDevicesPagination((prev) => ({ ...prev, page: 0 }));
      isDeviceSearchTypingRef.current = false;
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [deviceSearch]);

  const hasDeviceFilters = useMemo(
    () => !!debouncedSearch || Object.keys(deviceColumnFilters).length > 0,
    [debouncedSearch, deviceColumnFilters],
  );

const imeiSearch = deviceColumnFilters?.imei;
const searchValue = debouncedSearch || imeiSearch;

  const { data: vehicleListData, isLoading: isVehicleListLoading } =
    useQueryGetVehicleList(
      clientId
        ? {
            clientId,
            size: devicesPagination.size,
            page: devicesPagination.page,
            sortBy: devicesSorting.sortBy ?? "ID",
            sort: devicesSorting.sortDir ?? "DESC",
              ...(searchValue ? { search: searchValue } : {}),
              ...(imeiSearch ? { isByImei: true } : {}),
              // ...deviceColumnFilters,
          }
        : undefined,
      { enabled: !!clientId },
    );

  const vehicleListResponse = vehicleListData?.data?.response;

  const deviceRows = useMemo(
    () => getCustomerDevices(vehicleListResponse),
    [vehicleListResponse],
  );

  const devicesTotalCount = getVehicleTotalCount(
    vehicleListResponse,
    deviceRows.length,
  );

  const applyDeviceColumnSearch = (column: string, value: unknown) => {
    const normalizedValue = String(value ?? "").trim();

    setDeviceColumnFilters((prev) => {
      const nextFilters = { ...prev };

      if (normalizedValue) {
        nextFilters[column] = normalizedValue;
      } else {
        delete nextFilters[column];
      }

      return nextFilters;
    });

    setDevicesPagination((prev) => ({ ...prev, page: 0 }));
  };

  const resetDeviceSearchAndFilters = () => {
    isDeviceSearchTypingRef.current = false;
    setDeviceSearch("");
    setDebouncedSearch("");
    setDeviceColumnFilters({});
    setDevicesPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <div className="bg-white rounded-[16px]">
      <div className="flex items-center justify-between gap-4 px-5 pt-3">
        <CustomTypography.CustomText className="!text-[17.72px] !font-bold">
          {t("devices")}
        </CustomTypography.CustomText>
      </div>

      <div className="flex items-center justify-between gap-4 px-5 my-6">
        <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIcon className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]" />
            ),
          }}
          prefix={
            <SearchIcon className="customers-search-icon w-[14px] h-[14px]" />
          }
          placeholder={t("search_device")}
          value={deviceSearch}
          className="global-filter-search-class !mt-0 !rounded-[4px]"
          onChange={(event) => {
            isDeviceSearchTypingRef.current = true;
            setDeviceSearch(event.target.value);
          }}
        />

        <div className="flex gap-2">
          {hasDeviceFilters && <CustomButton
            label={t("reset")}
            buttonSize="medium"
            disabled={!hasDeviceFilters}
            className="customer-detail-reset-btn"
            onClick={resetDeviceSearchAndFilters}
          />}

          {moveAuthorized && (
            <CustomTooltip
              title={t("move_to_another_customer")}
              placement="top"
            >
              <span className="inline-flex">
                <CustomButton
                  type="primary"
                  label={t("move")}
                  buttonSize="medium"
                  className="!text-white"
                  disabled={selectedVehicleIds.length === 0}
                  onClick={() => setMoveModalOpen(true)}
                />
              </span>
            </CustomTooltip>
          )}

          <CustomButton
            type="primary"
            label={t("install")}
            buttonSize="medium"
            className="customer-detail-install-btn !text-white"
          />
        </div>
      </div>

      <DevicesTable
        data={deviceRows}
        isLoading={isVehicleListLoading}
        pageSize={devicesPagination.size}
        currentPage={devicesPagination.page}
        totalCount={devicesTotalCount}
        handlePagination={(newPage) =>
          setDevicesPagination((prev) => ({ ...prev, page: newPage }))
        }
        handlePageSizeChange={(newSize) =>
          setDevicesPagination({ page: 0, size: Number(newSize) })
        }
        sorting={devicesSorting}
        setSorting={setDevicesSorting}
        columnFilters={deviceColumnFilters}
        onColumnSearchApply={applyDeviceColumnSearch}
        onColumnSearchReset={resetDeviceSearchAndFilters}
        selectionEnabled={moveAuthorized}
        selectedRowKeys={selectedVehicleIds}
        onSelectedRowKeysChange={setSelectedVehicleIds}
        onDeviceClick={(device) => {
          if (!clientId || !device.unitId || !device.vehicleId) return;

          navigate(
            `/customers/${encodeURIComponent(clientId)}/${encodeURIComponent(device.unitId)}/${encodeURIComponent(device.vehicleId)}`,
            {
              state: {
                customer: (
                  location.state as { customer?: unknown } | null
                )?.customer,
                device,
              },
            },
          );
        }}
      />

      {clientId && moveAuthorized && (
        <MoveVehiclesModal
          open={moveModalOpen}
          currentCustomerId={clientId}
          currentCustomer={currentCustomer}
          vehicleIds={selectedVehicleIds.filter(
            (id): id is string | number =>
              typeof id === "string" || typeof id === "number",
          )}
          onCancel={() => setMoveModalOpen(false)}
          onSuccess={() => {
            setMoveModalOpen(false);
            setSelectedVehicleIds([]);
          }}
        />
      )}
    </div>
  );
};

export default DevicesTab;
